/*eslint-disable*/

import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { useEffect, useMemo, useState, useCallback } from 'react';
// utils
import { fetcher, endpoints } from 'src/utils/api-client';
import { getBlob, parseContentDisposition, downloadBlob, getResponseHeader } from 'src/utils/download';

// ----------------------------------------------------------------------

export function useGetDevices() {
  const URL = endpoints.device.list;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const memoizedValue = useMemo(
    () => ({
      devices: data || [],
      devicesLoading: isLoading,
      devicesError: error,
      devicesValidating: isValidating,
      devicesEmpty: !isLoading && !data.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useGetDeviceWithConfigs(id, enabled) {
  const shouldFetch = Boolean(id) && enabled;
  const key = shouldFetch
    ? endpoints.device.deviceWithConfigs(id)
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return useMemo(
    () => ({
      deviceWithConfigs: data || {},
      deviceWithConfigsLoading: isLoading,
      deviceWithConfigsError: error,
      deviceWithConfigsValidating: isValidating,
      refetchDeviceWithConfigs: mutate, 
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

// ----------------------------------------------------------------------

export function useDownloadBackup() {
  const mutationFn = async (key, { arg }) => {
    const { backupId, fileName } = arg;

    const url = endpoints.device.deviceBackup(backupId);

    const res = await getBlob(url);

    const cd = getResponseHeader(res.headers, 'content-disposition');
    const filename = parseContentDisposition(cd, fileName || `backup-${backupId}.bin`);

    downloadBlob(res.data, filename);
    return { fileName: filename };
  };

  const { trigger, isMutating, error, data, reset } =
    useSWRMutation('device-backup-download', mutationFn);

  return { trigger, isMutating, error, data, reset };
}

// ----------------------------------------------------------------------

export function useGetDeviceBackups(device_id) {
  const key = endpoints.device.deviceBackups(device_id)

  const { data, isLoading, error, isValidating, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const memoizedValue = useMemo(
    () => ({
      backupsList: data || [],
      backupsListLoading: isLoading,
      backupsListError: error,
      backupsListValidating: isValidating,
      backupsListEmpty: !isLoading && !data?.length,
      refetchBackupsList: mutate,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useListDbBackups(deviceUin) {
  const key = deviceUin ? endpoints.device.listDbBackups(deviceUin) : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const memoizedValue = useMemo(
    () => ({
      dbBackupsList: data || [],
      dbBackupsListLoading: isLoading,
      dbBackupsListError: error,
      dbBackupsListValidating: isValidating,
      dbBackupsListEmpty: !isLoading && !data?.length,
      refetchDbBackupsList: mutate,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useListControllerBackups(deviceUin) {
  const key = endpoints.device.listControllerBackups(deviceUin)

  const { data, isLoading, error, isValidating, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: false
  });

  const memoizedValue = useMemo(
    () => ({
      controllerBackupsList: data || {},
      controllerBackupsListLoading: isLoading,
      controllerBackupsListError: error,
      controllerBackupsListValidating: isValidating,
      controllerBackupsListEmpty: !isLoading && !data?.length,
      refetchControllerBackupsList: mutate,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
};
